import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Button, TextField, Typography } from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import CheckIcon from '@material-ui/icons/Check';
import ReplayIcon from '@material-ui/icons/Replay';
import ReceiptOutlinedIcon from '@material-ui/icons/ReceiptOutlined';
import moment from 'moment';

import VisorPdf from '../../Impresion/VisorPdf/VisorPdf';
import LineaImputacion from '../LineaImputacion/LineaImputacion';
import { EstadoEiffelChip, EstadoFacturaChip } from '../EstadoSmartScan/EstadoSmartScan';
import { camposFaltantes, estadoFactura, estadosFactura, formatFecha, formatImporte, gruposLineas, pdfFactura, problemasFactura } from '../datosSmartScan';
import colores from '../../Impresion/Bandeja/coloresBandeja';

const useStyles = makeStyles((theme) => ({
  panel: {
    backgroundColor: 'white',
    border: `1px solid ${colores.borde}`,
    borderRadius: '6px',
    boxShadow: colores.sombra,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    [theme.breakpoints.down('md')]: {
      overflowY: 'auto'
    }
  },
  vacio: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px',
    color: colores.tenue
  },
  volver: {
    alignSelf: 'flex-start',
    margin: '8px 8px 0',
    color: colores.secundario,
    textTransform: 'none'
  },
  cabecera: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '16px 20px 12px',
    borderBottom: `1px solid ${colores.linea}`,
    [theme.breakpoints.down('xs')]: {
      padding: '12px 16px'
    }
  },
  info: {
    flex: 1,
    minWidth: 0
  },
  tipo: {
    fontSize: '12px',
    color: colores.secundario,
    textTransform: 'uppercase',
    letterSpacing: '0.06em'
  },
  proveedor: {
    margin: '4px 0 2px',
    fontWeight: 500,
    fontSize: '20px',
    wordBreak: 'break-word',
    [theme.breakpoints.down('xs')]: {
      fontSize: '17px'
    }
  },
  sub: {
    fontSize: '12px',
    color: colores.tenue
  },
  totalCabecera: {
    textAlign: 'right',
    fontSize: '22px',
    fontWeight: 500,
    whiteSpace: 'nowrap',
    '& div': {
      marginTop: '4px'
    }
  },
  cuerpo: {
    flex: 1,
    minHeight: 0,
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
      flex: 'none'
    }
  },
  columnaPdf: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    backgroundColor: '#525659',
    borderRight: `1px solid ${colores.borde}`,
    [theme.breakpoints.down('md')]: {
      height: '70vh',
      borderRight: 'none'
    }
  },
  abrirPdf: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '6px 12px',
    fontSize: '12px',
    color: 'white',
    backgroundColor: '#3c3f41',
    textDecoration: 'none'
  },
  columnaDatos: {
    overflowY: 'auto',
    minHeight: 0,
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    backgroundColor: '#fafafa',
    [theme.breakpoints.down('md')]: {
      overflowY: 'visible'
    },
    [theme.breakpoints.down('xs')]: {
      padding: '12px'
    }
  },
  tarjeta: {
    backgroundColor: 'white',
    border: `1px solid ${colores.borde}`,
    borderRadius: '6px'
  },
  tituloTarjeta: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '10px 12px',
    fontSize: '12px',
    fontWeight: 'bold',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: colores.secundario,
    borderBottom: `1px solid ${colores.linea}`
  },
  campos: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: '12px',
    padding: '14px 12px',
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: 'minmax(0, 1fr)'
    }
  },
  ancho: {
    gridColumn: '1 / -1'
  },
  faltante: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#fff8d6'
    }
  },
  aviso: {
    padding: '10px 12px',
    borderRadius: '6px',
    fontSize: '13px',
    '& ul': {
      margin: '4px 0 0',
      paddingLeft: '18px'
    }
  },
  avisoAmarillo: {
    backgroundColor: '#fff8d6',
    border: '1px solid #f5c518'
  },
  avisoRojo: {
    backgroundColor: '#ffebee',
    border: `1px solid ${colores.error}`,
    color: '#b71c1c'
  },
  avisoVerde: {
    backgroundColor: '#e8f5e9',
    border: `1px solid ${colores.ok}`,
    color: '#1b5e20'
  },
  avisoCeleste: {
    backgroundColor: colores.primarioClaro,
    border: `1px solid ${colores.primario}`,
    color: colores.primarioOscuro
  },
  aprobar: {
    padding: '10px',
    fontWeight: 'bold'
  }
}));

const camposCabecera = [
  { campo: 'cuit', etiqueta: 'CUIT' },
  { campo: 'proveedor', etiqueta: 'Proveedor', ancho: true },
  { campo: 'numero', etiqueta: 'Número' },
  { campo: 'fecha', etiqueta: 'Fecha', tipo: 'date' },
  { campo: 'importeNeto', etiqueta: 'Importe neto', tipo: 'number' },
  { campo: 'total', etiqueta: 'Total', tipo: 'number' },
  { campo: 'moneda', etiqueta: 'Moneda', opciones: ['ARS', 'USD', 'EUR'] },
  { campo: 'cotizacion', etiqueta: 'Cotización', tipo: 'number' }
];

const textoBotonAprobar = (estado) => {
  if (estado === estadosFactura.faltanDatos) return 'Corregí lo de arriba para aprobar';
  if (estado === estadosFactura.pendiente) return 'Resolvé todo lo pendiente para aprobar';
  return 'Aprobar y enviar a Eiffel';
};

const EstadoEiffel = ({ factura, classes, onReintentar }) => {
  const { eiffel } = factura;
  if (!eiffel) return null;
  if (eiffel.estado === 'grabado') {
    return (
      <div className={`${classes.aviso} ${classes.avisoVerde}`}>
        <b>✓ Grabada en Eiffel</b> — {`EiffelKey: ${eiffel.eiffelKey} · ${moment(eiffel.fecha).format('DD/MM/YYYY HH:mm')}`}
      </div>
    );
  }
  if (eiffel.estado === 'error') {
    return (
      <div className={`${classes.aviso} ${classes.avisoRojo}`}>
        <b>⚠ No se pudo grabar en Eiffel.</b> Se reintenta automáticamente.
        <div style={{ marginTop: '4px' }}>{eiffel.error}</div>
        <Button size="small" startIcon={<ReplayIcon />} style={{ marginTop: '6px' }} onClick={onReintentar}>
          Reintentar ahora
        </Button>
      </div>
    );
  }
  return (
    <div className={`${classes.aviso} ${classes.avisoCeleste}`}>
      <b>En cola:</b> se graba en Eiffel en los próximos minutos.
    </div>
  );
};

/**
 * Detalle de una factura: el PDF original a la izquierda y lo que leyó SmartScan a la derecha (cabecera, líneas,
 * impuestos y percepciones con su ítem de compra). Las aprobadas se ven sólo lectura con su estado en Eiffel.
 */
const DetalleFactura = ({ factura, onVolver, onEditar, onAsignar, onAprobar, onReintentar }) => {
  const classes = useStyles();

  if (!factura) {
    return (
      <div className={classes.panel}>
        <div className={classes.vacio}>
          <ReceiptOutlinedIcon style={{ fontSize: 48 }} />
          <Typography variant="body2">Elegí una factura de la lista</Typography>
        </div>
      </div>
    );
  }

  const soloLectura = factura.estado === 'aprobada';
  const estado = estadoFactura(factura);
  const faltantes = camposFaltantes(factura);
  const problemas = problemasFactura(factura);
  const pdf = pdfFactura(factura);

  const valorCampo = (campo, tipo) => {
    const valor = factura[campo];
    if (valor === null || valor === undefined) return '';
    return tipo === 'date' ? moment(valor).format('YYYY-MM-DD') : valor;
  };

  const editar = (campo, tipo, valor) => {
    if (tipo === 'number') onEditar(campo, valor === '' ? null : Number(valor));
    else onEditar(campo, valor || null);
  };

  return (
    <div className={classes.panel}>
      {onVolver && (
        <Button className={classes.volver} size="small" startIcon={<ArrowBackIcon />} onClick={onVolver}>
          Volver a la lista
        </Button>
      )}

      <div className={classes.cabecera}>
        <div className={classes.info}>
          <div className={classes.tipo}>{`${factura.tipo} ${factura.letra} · ${factura.numero || 'sin número'}`}</div>
          <div className={classes.proveedor}>{factura.proveedor || 'Proveedor sin identificar'}</div>
          <div className={classes.sub}>
            {`CUIT ${factura.cuit || '—'} · Fecha ${formatFecha(factura.fecha)} · Recibida por ${factura.origen.toLowerCase()} el ${moment(
              factura.recibida
            ).format('DD/MM HH:mm')}`}
            {factura.tieneQr ? ' · QR de AFIP leído' : ' · sin QR de AFIP'}
          </div>
        </div>
        <div className={classes.totalCabecera}>
          {formatImporte(factura.total)}
          <div>
            {soloLectura ? <EstadoEiffelChip eiffel={factura.eiffel} /> : <EstadoFacturaChip factura={factura} />}
          </div>
        </div>
      </div>

      <div className={classes.cuerpo}>
        <div className={classes.columnaPdf}>
          <a className={classes.abrirPdf} href={pdf.url} target="_blank" rel="noopener noreferrer">
            <OpenInNewIcon style={{ fontSize: 14 }} /> Abrir la factura en una pestaña nueva
          </a>
          <div style={{ flex: 1, minHeight: 0 }}>
            <VisorPdf pdf={pdf} />
          </div>
        </div>

        <div className={classes.columnaDatos}>
          {soloLectura && <EstadoEiffel factura={factura} classes={classes} onReintentar={onReintentar} />}

          {!soloLectura && factura.warnings.length > 0 && (
            <div className={`${classes.aviso} ${classes.avisoAmarillo}`}>
              <b>Revisar contra el PDF:</b>
              <ul>
                {factura.warnings.map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
            </div>
          )}

          <div className={classes.tarjeta}>
            <div className={classes.tituloTarjeta}>Datos generales</div>
            {!soloLectura && faltantes.length > 0 && (
              <div className={`${classes.aviso}`} style={{ paddingBottom: 0, color: '#8a6d00' }}>
                ⚠ Faltan datos que la lectura automática no encontró: completalos mirando el PDF.
              </div>
            )}
            <div className={classes.campos}>
              {camposCabecera.map(({ campo, etiqueta, tipo, opciones, ancho }) => (
                <TextField
                  key={campo}
                  label={etiqueta}
                  size="small"
                  variant="outlined"
                  type={tipo || 'text'}
                  select={!!opciones}
                  SelectProps={opciones ? { native: true } : undefined}
                  InputLabelProps={{ shrink: true }}
                  disabled={soloLectura}
                  value={valorCampo(campo, tipo)}
                  onChange={(event) => editar(campo, tipo, event.target.value)}
                  className={`${ancho ? classes.ancho : ''} ${faltantes.includes(campo) ? classes.faltante : ''}`}
                >
                  {opciones && opciones.map((opcion) => <option key={opcion} value={opcion}>{opcion}</option>)}
                </TextField>
              ))}
            </div>
          </div>

          {gruposLineas
            .filter((grupo) => factura[grupo.clave].length > 0)
            .map((grupo) => {
              const lineas = factura[grupo.clave];
              const asignadas = lineas.filter((linea) => linea.itemCompra).length;
              return (
                <div key={grupo.clave} className={classes.tarjeta}>
                  <div className={classes.tituloTarjeta}>
                    <span>{grupo.titulo}</span>
                    <span>{`${asignadas}/${lineas.length} asignadas`}</span>
                  </div>
                  {lineas.map((linea) => (
                    <LineaImputacion
                      key={linea.id}
                      linea={linea}
                      texto={grupo.texto(linea)}
                      detalle={grupo.detalle(linea)}
                      soloLectura={soloLectura}
                      onAsignar={(itemCompra) => onAsignar(grupo.clave, linea, itemCompra)}
                    />
                  ))}
                </div>
              );
            })}

          {!soloLectura && problemas.length > 0 && (
            <div className={`${classes.aviso} ${classes.avisoRojo}`}>
              <b>⚠ Esto no puede faltar para grabar en Eiffel:</b>
              <ul>
                {problemas.map((problema) => (
                  <li key={problema}>{problema}</li>
                ))}
              </ul>
            </div>
          )}

          {!soloLectura && (
            <Button
              fullWidth
              color="primary"
              variant="contained"
              className={classes.aprobar}
              startIcon={estado === estadosFactura.lista ? <CheckIcon /> : undefined}
              disabled={estado !== estadosFactura.lista}
              onClick={onAprobar}
            >
              {textoBotonAprobar(estado)}
            </Button>
          )}

          {soloLectura && (
            <Typography variant="caption" style={{ color: colores.tenue }}>
              {`Aprobada por ${factura.aprobadaPor} el ${moment(factura.aprobadaEl).format('DD/MM/YYYY HH:mm')}`}
            </Typography>
          )}
        </div>
      </div>
    </div>
  );
};

export default DetalleFactura;
